import { useEffect, useState, useRef } from "react";
import { verifyEmail } from "wasp/client/auth";
import { useLocation } from "react-router-dom";
import { Link as WaspRouterLink, routes } from "wasp/client/router";
import { AuthPageLayout } from "../AuthPageLayout";
import useTranslation from "../../client/hooks/useTranslation";
import { Button } from "../../client/components/ui/button";
import { resendVerificationEmail } from "wasp/client/operations";

type VerificationState = "verifying" | "success" | "error" | "missing-token";
type ResendStatus = "idle" | "loading" | "success" | "error";

export function EmailVerificationPage() {
  const t = useTranslation();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const email = (location.state as { email?: string } | null)?.email ?? null;

  const [state, setState] = useState<VerificationState>(
    token ? "verifying" : "missing-token",
  );
  const [resendStatus, setResendStatus] = useState<ResendStatus>("idle");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!token) return;
    verifyEmail({ token })
      .then(() => setState("success"))
      .catch(() => setState("error"));
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  function startCooldown(seconds: number) {
    setCooldownSeconds(seconds);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleResend() {
    if (!email || cooldownSeconds > 0 || resendStatus === "loading") return;
    setResendStatus("loading");
    try {
      const result = await resendVerificationEmail({ email });
      if (result.success) {
        setResendStatus("success");
        startCooldown(60);
      } else {
        setResendStatus("idle");
        startCooldown(result.timeLeft);
      }
    } catch {
      setResendStatus("error");
    }
  }

  const resendLabel =
    cooldownSeconds > 0
      ? t.auth.resendEmailCooldown.replace("{seconds}", String(cooldownSeconds))
      : t.auth.resendEmail;

  return (
    <AuthPageLayout>
      <div className="pt-8 space-y-4 text-center">
        {state === "verifying" && (
          <p className="text-sm text-gray-500">{t.auth.emailVerificationVerifying}</p>
        )}

        {state === "missing-token" && (
          <>
            <p className="text-sm text-gray-700">{t.auth.emailVerificationPending}</p>
            <p className="text-xs text-gray-500">{t.auth.emailVerificationCheckSpam}</p>

            {email && (
              <div className="pt-2 space-y-2">
                <Button
                  variant="default"
                  size="sm"
                  disabled={cooldownSeconds > 0 || resendStatus === "loading"}
                  onClick={handleResend}
                >
                  {resendStatus === "loading" ? t.auth.resendEmailLoading : resendLabel}
                </Button>

                {resendStatus === "success" && (
                  <p className="text-xs text-primary">{t.auth.resendEmailSuccess}</p>
                )}
                {resendStatus === "error" && (
                  <p className="text-xs text-red-500">{t.auth.resendEmailError}</p>
                )}
              </div>
            )}

            <div className="pt-2">
              <WaspRouterLink
                to={routes.LoginRoute.to}
                className="text-sm underline text-foreground hover:opacity-80"
              >
                {t.auth.goToLogin}
              </WaspRouterLink>
            </div>
          </>
        )}

        {state === "success" && (
          <>
            <p className="text-sm font-medium text-primary">{t.auth.emailVerificationSuccess}</p>
            <WaspRouterLink
              to={routes.LoginRoute.to}
              className="text-sm underline text-foreground hover:opacity-80"
            >
              {t.auth.goToLogin}
            </WaspRouterLink>
          </>
        )}

        {state === "error" && (
          <p className="text-sm text-red-500">{t.auth.emailVerificationError}</p>
        )}
      </div>
    </AuthPageLayout>
  );
}
